import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import type { WebPage, WithContext } from 'schema-dts';
import { getOriginUrl } from '../url-utils';
import type { FAQSchemaItem } from '../../pages/gift-cards/components/GiftCardsFAQSection/GiftCardsFAQSection';
import type { BreadcrumbsItemsList } from '../../ui-components/breadcrumbs/types';

type GiftCardsSchemaProps = {
    breadcrumbTrail: BreadcrumbsItemsList[];
    faqItems: FAQSchemaItem[];
};

export const GiftCardsSchema = ({
    breadcrumbTrail,
    faqItems,
}: GiftCardsSchemaProps): JSX.Element => {
    const originUrl = getOriginUrl();
    const intl = useIntl();

    const jsonLd: WithContext<WebPage> = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Gift Cards',
        description: intl.formatMessage({
            id: 'gift_cards.schema.description',
            defaultMessage:
                'Explore our gift cards, perfect for any occasion. Choose from physical or electronic options.',
        }),
        url: `${originUrl}/gift-cards`,
        breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbTrail.map((item, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                name: item.label,
                item: `${originUrl}${item.href}`,
            })),
        },
        mainEntity: {
            '@type': 'FAQPage',
            mainEntity: faqItems.map((item) => ({
                '@type': 'Question',
                name: item.title,
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: item.answer,
                },
            })),
        },
    };

    return (
        <Helmet>
            <script type="application/ld+json" data-testid="schema-script">
                {JSON.stringify(jsonLd, null, 2)}
            </script>
        </Helmet>
    );
};
